import { useCallback } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import { extractAddress, type ParsedAddress } from './addressUtils';

const libraries = ['places'] as const;
const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_CLIENT_KEY || '';

/**
 * Hook to resolve an address string to a ParsedAddress using the Places API.
 * Uses AutocompleteService + PlacesService (not the Geocoding API) so it works
 * with API keys that only have the Places API enabled.
 */
export const useGeocode = () => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: libraries as any,
    ...(GOOGLE_MAPS_API_KEY ? {} : { preventLoad: true }),
  });

  const geocode = useCallback(
    async (address: string): Promise<ParsedAddress | null> => {
      if (!isLoaded || !address.trim()) return null;

      // Step 1: Get place predictions using AutocompleteService
      const prediction = await new Promise<google.maps.places.AutocompletePrediction | null>((resolve) => {
        const service = new google.maps.places.AutocompleteService();
        service.getPlacePredictions(
          { input: address, types: ['address'], componentRestrictions: { country: 'us' } },
          (predictions, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && predictions?.[0]) {
              resolve(predictions[0]);
            } else {
              resolve(null);
            }
          },
        );
      });

      if (!prediction) return null;

      // Step 2: Get full place details (address components, geometry) using PlacesService
      return new Promise((resolve) => {
        const placesService = new google.maps.places.PlacesService(document.createElement('div'));
        placesService.getDetails(
          { placeId: prediction.place_id, fields: ['formatted_address', 'address_components', 'geometry'] },
          (details, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && details?.address_components) {
              const parsed = extractAddress(details.address_components, details.formatted_address || '', address);
              if (details.geometry?.location) {
                parsed.latitude = details.geometry.location.lat();
                parsed.longitude = details.geometry.location.lng();
              }
              resolve(parsed);
            } else {
              resolve(null);
            }
          },
        );
      });
    },
    [isLoaded],
  );

  return { geocode, isLoaded };
};
